/* File: src/pages/RegisterPage.js
   Purpose: New registration page for Experts4Germany.
   Captures: Email, Password, Role (dropdown: Applicant, Employer, Press) and Country (to obtain the 2-digit ISO code).
   The backend handles: userID generation (LLxxxxxxR), IP capture, creation date, status ('new'), password encryption, and JWT generation.
*/

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from "../config/config";

const RegisterPage = () => {
  const navigate = useNavigate();
  const [role, setRole] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isoCode, setIsoCode] = useState('');
  const [countries, setCountries] = useState([]);
  const [errorMsg, setErrorMsg] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load country list from the database
  useEffect(() => {
    axios.get(`${config.API_BASE_URL}/api/countries`)
      .then(response => {
        setCountries(response.data);
      })
      .catch(error => {
        console.error("Error loading countries:", error);
      });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    if (!role || !email || !password || !isoCode) {
      setErrorMsg("Please fill all fields.");
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        email,
        password,
        role,       // expects one of: 'applicant', 'employer', 'press'
        iso_code: isoCode
      };
      // POST to the registration endpoint. The backend will generate the userID, store the IP,
      // set creation date, status ('new'), encrypt the password and return a JWT.
      const response = await axios.post(`${config.API_BASE_URL}/api/register`, payload);
      const { token, user } = response.data;
      // Save the token and user information in localStorage
      localStorage.setItem("token", token);
      localStorage.setItem("userid", user.userid);
      localStorage.setItem("role", user.role);
      
      // Redirect based on user role
      if (user.role === 'applicant') {
        navigate('/dashboard/applicant');
      } else if (user.role === 'employer') {
        navigate('/dashboard/employer');
      } else if (user.role === 'press') {
        navigate('/dashboard/press');
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error("Registration error:", error);
      setErrorMsg(error.response?.data?.error || "Registration failed.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex h-screen justify-center items-center">
      <div className="bg-black bg-opacity-50 p-8 rounded-lg shadow-lg w-96 text-center">
        <h2 className="text-2xl font-bold mb-4 text-white">Register</h2>
        {errorMsg && <p className="text-red-500 mb-4">{errorMsg}</p>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <input 
            type="email"
            name="email"
            placeholder="E-Mail address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full p-2 border border-gray-300 rounded bg-gray-800 text-white"
          />
          <input 
            type="password"
            name="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full p-2 border border-gray-300 rounded bg-gray-800 text-white"
          />
          <div className="w-full">
            <label className="block text-white mb-1">Role</label>
            <select 
              name="role" 
              value={role} 
              onChange={(e) => setRole(e.target.value)}
              required
              className="w-full p-2 border border-gray-300 rounded bg-gray-800 text-white"
            >
              <option value="">Select Role</option>
              <option value="applicant">Applicant</option>
              <option value="employer">Employer</option>
              <option value="press">Press</option>
            </select>
          </div>
          <div className="w-full">
            <label className="block text-white mb-1">Country</label>
            <select 
              name="iso_code" 
              value={isoCode} 
              onChange={(e) => setIsoCode(e.target.value)}
              required
              className="w-full p-2 border border-gray-300 rounded bg-gray-800 text-white"
            >
              <option value="">Select Country</option>
              {countries.map((country) => (
                <option key={country.iso_code} value={country.iso_code}>
                  {country.name} ({country.iso_code})
                </option>
              ))}
            </select>
          </div>
          <button 
            type="submit" 
            className="w-full p-2 bg-teal-400 text-white rounded hover:bg-teal-500 transition mt-4"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Registering..." : "Register"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default RegisterPage;
